import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";

/**
 * FOTO 3x4 — tirar (ou escolher) e enquadrar antes de mandar ao DP.
 *
 * A foto sai SEMPRE no formato 3x4 (600 × 800 px, JPEG): é a proporção do
 * crachá e da ficha. A pessoa arrasta a imagem e ajusta o zoom até o rosto
 * ficar no quadro — sem isso, a foto de celular (vertical, com metade do corpo)
 * cortada no centro sai com a testa de fora.
 *
 * O arquivo é redesenhado num `<canvas>`: o que vai ao servidor é um JPEG leve,
 * nunca a foto original de 12 MP — e nunca HEIC, que o Chrome não abre.
 */

interface Props {
  onEnviada: (mensagem: string) => void;
  onCancelar: () => void;
}

const PREVIA_L = 240;
const PREVIA_A = 320;
const SAIDA_L = 600;
const SAIDA_A = 800;

export default function FotoTresPorQuatro({ onEnviada, onCancelar }: Props) {
  const [imagem, setImagem] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [desloc, setDesloc] = useState({ x: 0, y: 0 });
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const canvas = useRef<HTMLCanvasElement>(null);
  const arrasto = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const camera = useRef<HTMLInputElement>(null);
  const galeria = useRef<HTMLInputElement>(null);

  /** Geometria da imagem dentro de um quadro L×A, com o zoom e o deslocamento. */
  function geometria(img: HTMLImageElement, L: number, A: number, dx: number, dy: number, z: number) {
    const escala = Math.max(L / img.naturalWidth, A / img.naturalHeight) * z;
    const w = img.naturalWidth * escala;
    const h = img.naturalHeight * escala;
    // O deslocamento é guardado em pixels da PRÉVIA; na saída ele é reescalado.
    const fx = L / PREVIA_L;
    const fy = A / PREVIA_A;
    return { x: (L - w) / 2 + dx * fx, y: (A - h) / 2 + dy * fy, w, h };
  }

  /** Limita o arrasto para a imagem nunca deixar borda vazia no quadro. */
  function limitar(img: HTMLImageElement, dx: number, dy: number, z: number) {
    const { w, h } = geometria(img, PREVIA_L, PREVIA_A, 0, 0, z);
    const mx = Math.max(0, (w - PREVIA_L) / 2);
    const my = Math.max(0, (h - PREVIA_A) / 2);
    return { x: Math.min(mx, Math.max(-mx, dx)), y: Math.min(my, Math.max(-my, dy)) };
  }

  useEffect(() => {
    const c = canvas.current;
    if (!c || !imagem) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const g = geometria(imagem, PREVIA_L, PREVIA_A, desloc.x, desloc.y, zoom);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, PREVIA_L, PREVIA_A);
    ctx.drawImage(imagem, g.x, g.y, g.w, g.h);
  }, [imagem, zoom, desloc]);

  function carregar(files: FileList | null) {
    const arquivo = files?.[0];
    if (!arquivo) return;
    setErro(null);
    if (!arquivo.type.startsWith("image/")) {
      setErro("Escolha uma imagem (foto).");
      return;
    }
    const url = URL.createObjectURL(arquivo);
    const img = new Image();
    img.onload = () => {
      setImagem(img);
      setZoom(1);
      setDesloc({ x: 0, y: 0 });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      // Foto HEIC do iPhone aberta num navegador que não a lê.
      setErro("Não foi possível abrir essa imagem. Use o botão \"Tirar foto\" ou escolha uma foto JPG.");
    };
    img.src = url;
  }

  function mudarZoom(z: number) {
    if (!imagem) return;
    setZoom(z);
    setDesloc((d) => limitar(imagem, d.x, d.y, z));
  }

  async function enviar() {
    if (!imagem) return;
    const saida = document.createElement("canvas");
    saida.width = SAIDA_L;
    saida.height = SAIDA_A;
    const ctx = saida.getContext("2d");
    if (!ctx) return;
    const g = geometria(imagem, SAIDA_L, SAIDA_A, desloc.x, desloc.y, zoom);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, SAIDA_L, SAIDA_A);
    ctx.drawImage(imagem, g.x, g.y, g.w, g.h);

    setEnviando(true);
    setErro(null);
    try {
      const r = await api.pedirFoto(saida.toDataURL("image/jpeg", 0.85));
      onEnviada(r.mensagem);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível enviar a foto.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="painel-form">
      <p className="explica">
        Fundo claro, rosto de frente e centralizado, sem boné nem óculos escuros. A foto vale para o
        crachá e para o cadastro depois que o DP aprovar.
      </p>

      <input ref={camera} type="file" accept="image/*" capture="user" hidden onChange={(e) => carregar(e.target.files)} />
      <input ref={galeria} type="file" accept="image/jpeg,image/png" hidden onChange={(e) => carregar(e.target.files)} />

      {imagem ? (
        <>
          <div className="foto34-moldura">
            <canvas
              ref={canvas}
              width={PREVIA_L}
              height={PREVIA_A}
              className="foto34-canvas"
              onPointerDown={(e) => {
                (e.target as HTMLElement).setPointerCapture(e.pointerId);
                arrasto.current = { x: e.clientX, y: e.clientY, ox: desloc.x, oy: desloc.y };
              }}
              onPointerMove={(e) => {
                const a = arrasto.current;
                if (!a || !imagem) return;
                setDesloc(limitar(imagem, a.ox + e.clientX - a.x, a.oy + e.clientY - a.y, zoom));
              }}
              onPointerUp={() => (arrasto.current = null)}
              onPointerCancel={() => (arrasto.current = null)}
            />
          </div>
          <label className="campo">
            <span>Aproximar</span>
            <input
              type="range"
              min="1"
              max="3"
              step="0.05"
              value={zoom}
              onChange={(e) => mudarZoom(Number(e.target.value))}
            />
          </label>
          <p className="dica">Arraste a foto para centralizar o rosto.</p>
        </>
      ) : (
        <div className="foto34-vazia">Nenhuma foto escolhida</div>
      )}

      {erro && <div className="aviso-erro">{erro}</div>}

      <div className="botoes">
        <button className="botao-secundario" onClick={() => camera.current?.click()} disabled={enviando}>
          {imagem ? "Tirar outra" : "Tirar foto"}
        </button>
        <button className="botao-secundario" onClick={() => galeria.current?.click()} disabled={enviando}>
          Escolher da galeria
        </button>
      </div>
      <div className="botoes">
        <button className="botao-texto" onClick={onCancelar} disabled={enviando}>
          Cancelar
        </button>
        <button className="botao-principal" onClick={enviar} disabled={!imagem || enviando}>
          {enviando ? "Enviando…" : "Enviar para o DP"}
        </button>
      </div>
    </div>
  );
}
